import React, { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useSignIn } from '@clerk/clerk-react';
import { Lock, ShieldCheck } from 'lucide-react';
import { AuthCardLayout } from './layout/AuthCardLayout';
import { AuthErrorBanner } from './layout/AuthErrorBanner';
import { OAuthSocialButtons } from './forms/OAuthSocialButtons';
import { SignInEmailPasswordForm } from './forms/SignInEmailPasswordForm';
import { SignInMfaForm } from './forms/SignInMfaForm';

export const SignInPage: React.FC = () => {
  const { isLoaded, signIn, setActive } = useSignIn();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const redirectUrl = searchParams.get('redirect_url') || '/';

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [oauthLoading, setOauthLoading] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const [isMfaStep, setIsMfaStep] = useState(false);
  const [mfaCode, setMfaCode] = useState('');
  const [mfaStrategy, setMfaStrategy] = useState<'phone_code' | 'totp' | 'email_code'>('email_code');

  const handleEmailPasswordSignIn = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!isLoaded || !signIn) return;
    setIsLoading(true);
    setError(null);

    try {
      const result = await signIn.create({ identifier: email.trim(), password });
      if (result.status === 'complete') {
        await setActive({ session: result.createdSessionId });
        navigate(redirectUrl, { replace: true });
      } else if (result.status === 'needs_second_factor') {
        const factor = result.supportedSecondFactors?.find(
          (f) => f.strategy === 'email_code' || f.strategy === 'phone_code' || f.strategy === 'totp'
        );
        if (factor?.strategy === 'email_code' || factor?.strategy === 'phone_code') {
          await result.prepareSecondFactor({ strategy: factor.strategy });
          setMfaStrategy(factor.strategy);
        } else {
          setMfaStrategy('totp');
        }
        setIsMfaStep(true);
      } else {
        setError(result.status === 'needs_first_factor' ? 'Additional first factor verification is required.' : `Status: ${result.status}`);
      }
    } catch (err: any) {
      setError(err?.errors?.[0]?.longMessage || err?.errors?.[0]?.message || err?.message || 'Failed to sign in.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleMfaVerification = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!isLoaded || !signIn) return;
    setIsLoading(true);
    setError(null);

    try {
      const result = await signIn.attemptSecondFactor({ strategy: mfaStrategy, code: mfaCode.trim() });
      if (result.status === 'complete') {
        await setActive({ session: result.createdSessionId });
        navigate(redirectUrl, { replace: true });
      } else {
        setError(`Verification incomplete. Status: ${result.status}`);
      }
    } catch (err: any) {
      setError(err?.errors?.[0]?.longMessage || err?.errors?.[0]?.message || err?.message || 'Invalid code.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleOAuthSignIn = async (strategy: 'oauth_google') => {
    if (!isLoaded || !signIn) return;
    setOauthLoading(strategy);
    setError(null);
    try {
      await signIn.authenticateWithRedirect({ strategy, redirectUrl: '/sso-callback', redirectUrlComplete: redirectUrl });
    } catch (err: any) {
      setError(err?.errors?.[0]?.longMessage || err?.errors?.[0]?.message || err?.message || 'OAuth failed.');
      setOauthLoading(null);
    }
  };

  return (
    <AuthCardLayout
      title={isMfaStep ? 'Two-Factor Authentication' : 'Welcome Back'}
      subtitle={isMfaStep ? `Enter the verification code sent to your ${mfaStrategy === 'phone_code' ? 'phone' : 'email'}` : 'Sign in to access your partner earnings and referrals'}
      icon={isMfaStep ? <ShieldCheck className="w-6 h-6" /> : <Lock className="w-5 h-5" />}
      headerLinkPrompt="Need an account?"
      headerLinkAction="Sign up"
      headerLinkHref="/sign-up"
    >
      <AuthErrorBanner error={error} />
      {!isMfaStep ? (
        <>
          <OAuthSocialButtons onSelect={handleOAuthSignIn} isLoading={!isLoaded || isLoading} oauthLoading={oauthLoading} />
          <SignInEmailPasswordForm
            email={email}
            setEmail={setEmail}
            password={password}
            setPassword={setPassword}
            showPassword={showPassword}
            setShowPassword={setShowPassword}
            isLoading={!isLoaded || isLoading}
            onSubmit={handleEmailPasswordSignIn}
          />
        </>
      ) : (
        <SignInMfaForm
          mfaCode={mfaCode}
          setMfaCode={setMfaCode}
          isLoading={!isLoaded || isLoading}
          onSubmit={handleMfaVerification}
          onCancel={() => setIsMfaStep(false)}
        />
      )}
    </AuthCardLayout>
  );
};

export default SignInPage;
